import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { TooltipGroupRow, TooltipRow } from "./types";

// The tooltip tables live outside the generated type map, so query them
// through an untyped view of the same client.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const db = supabase as any;

export type TooltipGroupData = { group: TooltipGroupRow; tips: TooltipRow[] } | null;

/** Fetch a tooltip group and its enabled tips (admin-authored, cached). */
export function useTooltipGroup(groupKey: string) {
  return useQuery<TooltipGroupData>({
    queryKey: ["tooltip-group", groupKey],
    enabled: !!groupKey,
    queryFn: async () => {
      const { data: group, error } = await db
        .from("tooltip_groups")
        .select("*")
        .eq("key", groupKey)
        .maybeSingle();
      if (error) throw error;
      if (!group) return null;

      const { data: tips, error: tipsError } = await db
        .from("tooltips")
        .select("*")
        .eq("group_id", group.id)
        .eq("is_enabled", true)
        .order("sort_order", { ascending: true });
      if (tipsError) throw tipsError;

      return {
        group: group as unknown as TooltipGroupRow,
        tips: (tips ?? []) as unknown as TooltipRow[],
      };
    },
    staleTime: 5 * 60 * 1000,
  });
}

/** Admin editor view — all groups, including disabled ones. */
export function useTooltipGroups() {
  return useQuery<TooltipGroupRow[]>({
    queryKey: ["tooltip-groups"],
    queryFn: async () => {
      const { data, error } = await db
        .from("tooltip_groups")
        .select("*")
        .order("sort_order", { ascending: true });
      if (error) throw error;
      return (data ?? []) as unknown as TooltipGroupRow[];
    },
  });
}

/** Admin editor view — all tips in a group, including disabled ones. */
export function useTooltipsForGroup(groupId: string | null) {
  return useQuery<TooltipRow[]>({
    queryKey: ["tooltips", groupId],
    enabled: !!groupId,
    queryFn: async () => {
      const { data, error } = await db
        .from("tooltips")
        .select("*")
        .eq("group_id", groupId!)
        .order("sort_order", { ascending: true });
      if (error) throw error;
      return (data ?? []) as unknown as TooltipRow[];
    },
  });
}
